import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { SelectItem } from 'primeng/api';
import { StateSelectorService } from '@core/service/state-selector.service';
import { GetStatesResponse, StateViewModel } from '@core/model/getStatesResponse';

@Component({
    selector: 'my-nexus-state-selector',
    templateUrl: './state-selector.component.html',
    styleUrls: ['./state-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => StateSelectorComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class StateSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private subs = new SubSink();

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  disabled = false;
  loading = false;
  errorMsg: string | null = null;

  selectedCode: string | null = null;

  options: SelectItem<string>[] = [];

  constructor(private stateSelectorService: StateSelectorService, private ref: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadStates();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadStates(): void {
    this.loading = true;
    this.errorMsg = null;

    this.subs.sink = this.stateSelectorService
      .getStates()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.ref.markForCheck();
        })
      )
      .subscribe({
        next: (res: GetStatesResponse) => {
          const states = (res?.states ?? []).filter((s) => !!s?.state);
          this.options = states.map((s: StateViewModel) => ({
            value: s.state!,
            label: s.stateDescription ?? s.state!,
          }));
        },
        error: () => {
          this.errorMsg = 'Failed to load states. Please try again.';
        },
      });
  }

  writeValue(value: string | null): void {
    this.selectedCode = value ?? null;
    this.ref.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.ref.detectChanges();
  }

  onClear(): void {
    this.selectedCode = null;
    this.onChange(null);
    this.onTouched();
  }

  onSelect(nextValue: string): void {
    this.selectedCode = nextValue ?? null;
    this.onChange(this.selectedCode);
    this.onTouched();
  }
}
